import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Phone,
  Clock,
  User,
  Bot,
  Activity,
  Zap,
  BarChart3,
  Mic,
  Settings,
  Pause,
  Play,
  PhoneOff,
} from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';
import { StatusBadge } from '../components/ui/StatusBadge';
import { GlowButton } from '../components/ui/GlowButton';
import { AgentGraph } from '../components/agents/AgentGraph';
import { WaveformVisualizer } from '../components/audio/WaveformVisualizer';
import { AudioLevelMeter } from '../components/audio/AudioLevelMeter';
import { LiveTranscript } from '../components/transcript/LiveTranscript';
import { ToolTimeline } from '../components/timeline/ToolTimeline';
import { pageTransition } from '../hooks/useAnimation';
import { cn } from '../lib/utils';
import type {
  Call,
  TranscriptSegment,
  ToolCall,
  AgentExecution,
  AudioLevel,
} from '../types';

// Generate demo data for the monitored call
function generateMonitoredCall(callId: string): Call {
  return {
    id: callId,
    direction: 'inbound',
    status: 'live',
    participants: [
      { id: 'cust-1', name: 'Sarah Johnson', phoneNumber: '+44 7700 900123', type: 'customer' },
      { id: 'ai-1', name: 'AI Agent', phoneNumber: '', type: 'ai' },
    ],
    startTime: new Date(Date.now() - 120000).toISOString(),
    duration: 120,
    currentAgent: 'NetworkAgent',
    agentExecutionId: 'exec-001',
    transcriptId: 'trans-001',
    metadata: {
      customerId: 'C-78234',
      accountNumber: 'ACC-554321',
      reason: 'Slow Internet',
      satisfaction: 4.2,
      deflected: false,
    },
  };
}

function generateFullTranscript(): TranscriptSegment[] {
  const now = Date.now();
  return [
    {
      id: 't1',
      callId: 'call-001',
      speaker: 'customer',
      text: 'Hi there, my internet has been really slow for the past two days. I work from home and this is causing serious problems.',
      timestamp: new Date(now - 120000).toISOString(),
      confidence: 0.94,
      isPartial: false,
    },
    {
      id: 't2',
      callId: 'call-001',
      speaker: 'ai',
      text: 'I completely understand how frustrating that must be, especially when working from home. I\'m going to run a diagnostic on your line right away to identify what\'s causing the slowdown.',
      timestamp: new Date(now - 115000).toISOString(),
      confidence: 0.98,
      isPartial: false,
    },
    {
      id: 't3',
      callId: 'call-001',
      speaker: 'system',
      text: 'Network diagnostic initiated for customer C-78234, line VDSL-554321',
      timestamp: new Date(now - 114000).toISOString(),
      confidence: 1,
      isPartial: false,
    },
    {
      id: 't4',
      callId: 'call-001',
      speaker: 'customer',
      text: 'Thank you. It\'s been dropping in and out as well, not just slow.',
      timestamp: new Date(now - 100000).toISOString(),
      confidence: 0.91,
      isPartial: false,
    },
    {
      id: 't5',
      callId: 'call-001',
      speaker: 'ai',
      text: 'That\'s very helpful information. Intermittent drops combined with speed reduction often point to a line stability issue or congestion in your area. The diagnostic will check both.',
      timestamp: new Date(now - 94000).toISOString(),
      confidence: 0.97,
      isPartial: false,
    },
    {
      id: 't6',
      callId: 'call-001',
      speaker: 'system',
      text: 'Line test complete: SNR margin 4.2dB (below 6dB threshold). DLM profile: Speed mode. 23 retrains in 24h.',
      timestamp: new Date(now - 85000).toISOString(),
      confidence: 1,
      isPartial: false,
    },
    {
      id: 't7',
      callId: 'call-001',
      speaker: 'ai',
      text: 'I\'ve found the issue. Your line has a noise margin below the stable threshold, which is causing the disconnections. I can see 23 reconnections in the last 24 hours.',
      timestamp: new Date(now - 82000).toISOString(),
      confidence: 0.96,
      isPartial: false,
    },
    {
      id: 't8',
      callId: 'call-001',
      speaker: 'customer',
      text: 'That makes sense. Is there anything you can do to fix it?',
      timestamp: new Date(now - 76000).toISOString(),
      confidence: 0.93,
      isPartial: false,
    },
    {
      id: 't9',
      callId: 'call-001',
      speaker: 'ai',
      text: 'Absolutely. I\'m going to apply a stability profile to your line which should resolve the disconnections. This may reduce your speed slightly but will ensure a stable connection.',
      timestamp: new Date(now - 72000).toISOString(),
      confidence: 0.97,
      isPartial: false,
    },
    {
      id: 't10',
      callId: 'call-001',
      speaker: 'system',
      text: 'Applying DLM stability profile to line VDSL-554321...',
      timestamp: new Date(now - 70000).toISOString(),
      confidence: 1,
      isPartial: false,
    },
    {
      id: 't11',
      callId: 'call-001',
      speaker: 'ai',
      text: 'The stability profile has been applied. You should see improvements within the next 30 minutes. I\'ve also created a support ticket for our technical team to monitor your line.',
      timestamp: new Date(now - 65000).toISOString(),
      confidence: 0.98,
      isPartial: false,
    },
    {
      id: 't12',
      callId: 'call-001',
      speaker: 'customer',
      text: 'That\'s brilliant, thank you so much for your help!',
      timestamp: new Date(now - 58000).toISOString(),
      confidence: 0.95,
      isPartial: false,
    },
    {
      id: 't13',
      callId: 'call-001',
      speaker: 'ai',
      text: 'You\'re very welcome, Sarah! If you continue to experience issues after 30 minutes, please don\'t hesitate to call back. Your ticket number is TKT-2024-7891. Have a great day!',
      timestamp: new Date(now - 54000).toISOString(),
      confidence: 0.97,
      isPartial: false,
    },
  ];
}

function generateFullTools(): ToolCall[] {
  const now = Date.now();
  return [
    {
      id: 'tool-001',
      callId: 'call-001',
      toolName: 'authenticate_customer',
      params: { phoneNumber: '+447700900123', accountNumber: 'ACC-554321', pin: '****' },
      result: { authenticated: true, customerId: 'C-78234', tier: 'premium', authMethod: 'pin' },
      status: 'success',
      latency: 245,
      startTime: new Date(now - 120000).toISOString(),
      endTime: new Date(now - 119755).toISOString(),
      agentId: 'AuthAgent',
    },
    {
      id: 'tool-002',
      callId: 'call-001',
      toolName: 'get_account_details',
      params: { customerId: 'C-78234' },
      result: { plan: 'Fibre 900', monthlyCost: 45, contractEnd: '2025-06-01', address: '12 Oak Street, London' },
      status: 'success',
      latency: 189,
      startTime: new Date(now - 119500).toISOString(),
      endTime: new Date(now - 119311).toISOString(),
      agentId: 'AuthAgent',
    },
    {
      id: 'tool-003',
      callId: 'call-001',
      toolName: 'run_line_diagnostic',
      params: { lineId: 'VDSL-554321', testType: 'full_spectrum', includeHistory: true },
      result: {
        snrMargin: 4.2,
        attenuation: 12.5,
        syncSpeed: { up: 18900, down: 78900 },
        maxSpeed: { up: 21300, down: 81200 },
        retrains: 23,
        errors: { es: 45, ses: 12, uas: 0 },
        lineQuality: 'poor',
      },
      status: 'success',
      latency: 3400,
      startTime: new Date(now - 115000).toISOString(),
      endTime: new Date(now - 111600).toISOString(),
      agentId: 'NetworkAgent',
    },
    {
      id: 'tool-004',
      callId: 'call-001',
      toolName: 'check_area_status',
      params: { exchangeCode: 'EX-LDN-042', cabinetId: 'P12', includeMaintenance: true },
      result: { incidents: 0, maintenance: false, congestionLevel: 'low', weatherImpact: 'none' },
      status: 'success',
      latency: 520,
      startTime: new Date(now - 111000).toISOString(),
      endTime: new Date(now - 110480).toISOString(),
      agentId: 'NetworkAgent',
    },
    {
      id: 'tool-005',
      callId: 'call-001',
      toolName: 'apply_dlm_profile',
      params: { lineId: 'VDSL-554321', profile: 'stability', notifyCustomer: true },
      result: { applied: true, newProfile: 'stability', estimatedEffect: '30 minutes' },
      status: 'success',
      latency: 2800,
      startTime: new Date(now - 72000).toISOString(),
      endTime: new Date(now - 69200).toISOString(),
      agentId: 'NetworkAgent',
    },
    {
      id: 'tool-006',
      callId: 'call-001',
      toolName: 'create_ticket',
      params: { customerId: 'C-78234', category: 'line_fault', priority: 'medium', autoAssign: true },
      result: { ticketId: 'TKT-2024-7891', sla: '48h', assignedTeam: 'Line Maintenance', estimatedResponse: '4h' },
      status: 'success',
      latency: 380,
      startTime: new Date(now - 68000).toISOString(),
      endTime: new Date(now - 67620).toISOString(),
      agentId: 'TicketAgent',
    },
    {
      id: 'tool-007',
      callId: 'call-001',
      toolName: 'send_sms_confirmation',
      params: { phoneNumber: '+447700900123', ticketId: 'TKT-2024-7891', type: 'resolution_expected' },
      result: { sent: true, messageId: 'SMS-7891-001', deliveryStatus: 'delivered' },
      status: 'success',
      latency: 145,
      startTime: new Date(now - 67000).toISOString(),
      endTime: new Date(now - 66855).toISOString(),
      agentId: 'SupervisorAgent',
    },
  ];
}

function generateFullAgentExecution(): AgentExecution {
  return {
    id: 'exec-001',
    callId: 'call-001',
    nodes: [
      {
        id: 'auth',
        type: 'auth',
        name: 'AuthAgent',
        status: 'completed',
        latency: 245,
        startTime: new Date(Date.now() - 120000).toISOString(),
        endTime: new Date(Date.now() - 119755).toISOString(),
        output: { authenticated: true, customerId: 'C-78234', tier: 'premium' },
      },
      {
        id: 'network',
        type: 'network',
        name: 'NetworkAgent',
        status: 'completed',
        latency: 6720,
        startTime: new Date(Date.now() - 119500).toISOString(),
        endTime: new Date(Date.now() - 112780).toISOString(),
        output: { snrMargin: 4.2, retrains: 23, profileApplied: 'stability' },
      },
      {
        id: 'ticket',
        type: 'ticket',
        name: 'TicketAgent',
        status: 'completed',
        latency: 380,
        startTime: new Date(Date.now() - 68000).toISOString(),
        endTime: new Date(Date.now() - 67620).toISOString(),
        output: { ticketId: 'TKT-2024-7891', sla: '48h' },
      },
      {
        id: 'supervisor',
        type: 'supervisor',
        name: 'SupervisorAgent',
        status: 'completed',
        latency: 145,
        startTime: new Date(Date.now() - 67000).toISOString(),
        endTime: new Date(Date.now() - 66855).toISOString(),
        output: { smsSent: true, escalation: false },
      },
    ],
    connections: [
      { from: 'auth', to: 'network', status: 'completed', label: 'auth_ok' },
      { from: 'network', to: 'ticket', status: 'completed', label: 'fix_applied' },
      { from: 'ticket', to: 'supervisor', status: 'completed', label: 'notify' },
    ],
    startTime: new Date(Date.now() - 120000).toISOString(),
    endTime: new Date(Date.now() - 66855).toISOString(),
    status: 'completed',
  };
}

function DurationCounter({ startTime }: { startTime: string }) {
  const [seconds, setSeconds] = useState(() =>
    Math.floor((Date.now() - new Date(startTime).getTime()) / 1000)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(Math.floor((Date.now() - new Date(startTime).getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return (
    <span className="font-mono tabular-nums">
      {mins.toString().padStart(2, '0')}:{secs.toString().padStart(2, '0')}
    </span>
  );
}

// Sparkline component for latency history
function Sparkline({ data, color = '#e60000', width = 200, height = 40 }: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
}) {
  if (data.length < 2) return null;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Area fill */}
      <polygon
        points={`0,${height} ${points} ${width},${height}`}
        fill={`${color}15`}
      />
      {/* Current value dot */}
      <circle
        cx={width}
        cy={height - ((data[data.length - 1] - min) / range) * height}
        r="3"
        fill={color}
      />
    </svg>
  );
}

export function CallMonitor() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const callId = searchParams.get('call') || 'call-001';

  const [call] = useState<Call>(() => generateMonitoredCall(callId));
  const [transcript] = useState<TranscriptSegment[]>(generateFullTranscript);
  const [tools] = useState<ToolCall[]>(generateFullTools);
  const [agentExecution] = useState<AgentExecution>(generateFullAgentExecution);

  const [audioData, setAudioData] = useState(() => {
    const data = new Uint8Array(128);
    for (let i = 0; i < 128; i++) data[i] = Math.random() * 100 + 30;
    return data;
  });

  const [audioLevel] = useState<AudioLevel>({ left: 0.7, right: 0.65, peak: 0.85 });

  // Simulate audio data
  useEffect(() => {
    const interval = setInterval(() => {
      setAudioData(() => {
        const newData = new Uint8Array(128);
        for (let i = 0; i < 128; i++) {
          newData[i] = Math.random() * 180 + 40;
        }
        return newData;
      });
    }, 80);
    return () => clearInterval(interval);
  }, []);

  // Latency sparkline data
  const latencyData = useMemo(() => {
    return tools
      .filter((t) => t.latency > 0)
      .map((t) => t.latency);
  }, [tools]);

  const totalAgentLatency = agentExecution.nodes.reduce(
    (sum, n) => sum + n.latency,
    0
  );

  return (
    <motion.div
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
      className="p-6 space-y-6"
    >
      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <GlowButton
            variant="ghost"
            size="sm"
            icon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate('/')}
          >
            Back
          </GlowButton>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Call Monitor</h1>
            <p className="text-sm text-text-muted mt-1">
              Real-time call details and agent execution
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <GlowButton variant="secondary" size="sm" icon={<Pause className="w-4 h-4" />}>
            Hold
          </GlowButton>
          <GlowButton variant="primary" size="sm" icon={<PhoneOff className="w-4 h-4" />}>
            End Call
          </GlowButton>
        </div>
      </div>

      {/* Call Info Bar */}
      <GlassCard variant="elevated" padding="md">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Status</p>
            <StatusBadge status={call.status} size="md" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Duration</p>
            <p className="text-lg font-semibold text-text-primary">
              <DurationCounter startTime={call.startTime} />
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Customer</p>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-info" />
              <span className="text-sm font-medium text-text-primary">
                {call.participants.find((p) => p.type === 'customer')?.name}
              </span>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Current Agent</p>
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-vokentra" />
              <span className="text-sm font-medium text-text-primary">{call.currentAgent}</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Reason</p>
            <span className="text-sm text-text-primary">{call.metadata.reason}</span>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Satisfaction</p>
            <div className="flex items-center gap-1">
              <span className="text-lg font-semibold text-success">
                {call.metadata.satisfaction?.toFixed(1)}
              </span>
              <span className="text-xs text-text-muted">/5</span>
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Waveform + Audio Meters */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <GlassCard
          variant="elevated"
          className="lg:col-span-3"
          header={
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mic className="w-4 h-4 text-vokentra" />
                <span className="text-sm font-semibold text-text-primary">Audio Waveform</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-xs text-text-muted">
                  <span>Mode:</span>
                  {['bars', 'wave', 'circular', 'spectrum'].map((m) => (
                    <button
                      key={m}
                      className="px-2 py-0.5 rounded bg-white/5 hover:bg-white/10 capitalize text-[10px] transition-colors"
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          }
        >
          <WaveformVisualizer
            audioData={audioData}
            mode="bars"
            height={200}
            barCount={80}
            isActive={true}
          />
        </GlassCard>

        <div className="space-y-4">
          <GlassCard variant="default" padding="md">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-text-secondary">Input Level</span>
              <span className="text-xs font-mono text-text-muted">{Math.round(audioLevel.left * 100)}%</span>
            </div>
            <AudioLevelMeter level={audioLevel} height={150} segments={25} />
          </GlassCard>

          <GlassCard variant="default" padding="md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-text-secondary">Peak</span>
              <span className="text-xs font-mono text-warning">{Math.round(audioLevel.peak * 100)}%</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-success via-warning to-error"
                style={{ width: `${audioLevel.peak * 100}%` }}
              />
            </div>
          </GlassCard>
        </div>
      </div>

      {/* Agent Graph + Latency Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <GlassCard
          variant="elevated"
          className="lg:col-span-2"
          header={
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-vokentra" />
                <span className="text-sm font-semibold text-text-primary">Agent Execution Graph</span>
              </div>
              <span className="text-xs text-text-muted font-mono">{agentExecution.id}</span>
            </div>
          }
        >
          <AgentGraph execution={agentExecution} animated={true} className="w-full" />
        </GlassCard>

        <GlassCard variant="default" header={
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-warning" />
            <span className="text-sm font-semibold text-text-primary">Latency Metrics</span>
          </div>
        }>
          <div className="space-y-4">
            {/* Total latency */}
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-text-muted">Total Pipeline</span>
                <span className="text-text-primary font-mono font-semibold">
                  {totalAgentLatency > 1000
                    ? `${(totalAgentLatency / 1000).toFixed(1)}s`
                    : `${totalAgentLatency}ms`}
                </span>
              </div>
              <Sparkline data={[120, 180, 150, 220, 190, 250, 210, 180, 200, 170, 230, 190]} />
            </div>

            {/* Per-agent latencies */}
            <div className="space-y-2">
              {agentExecution.nodes.map((node) => (
                <div key={node.id}>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-text-secondary">{node.name}</span>
                    <span className="font-mono text-text-muted">
                      {node.latency > 1000
                        ? `${(node.latency / 1000).toFixed(1)}s`
                        : `${node.latency}ms`}
                    </span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-1">
                    <motion.div
                      className={cn(
                        'h-full rounded-full',
                        node.latency < 500
                          ? 'bg-success'
                          : node.latency < 2000
                          ? 'bg-warning'
                          : 'bg-error'
                      )}
                      initial={{ width: 0 }}
                      animate={{
                        width: `${Math.min((node.latency / 5000) * 100, 100)}%`,
                      }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Tool latency sparkline */}
            <div className="pt-2 border-t border-white/[0.06]">
              <div className="flex justify-between text-xs mb-2">
                <span className="text-text-muted">Tool Call Latencies</span>
                <span className="text-text-primary font-mono">
                  avg: {Math.round(latencyData.reduce((a, b) => a + b, 0) / latencyData.length)}ms
                </span>
              </div>
              <Sparkline
                data={latencyData.length > 0 ? latencyData : [100, 200, 150, 300]}
                color="#00bfff"
                width={220}
                height={50}
              />
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Transcript */}
      <GlassCard
        variant="default"
        header={
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-success" />
              <span className="text-sm font-semibold text-text-primary">Complete Transcript</span>
              <span className="text-xs text-success">● Live</span>
            </div>
            <span className="text-xs text-text-muted">
              {transcript.length} segments
            </span>
          </div>
        }
      >
        <LiveTranscript segments={transcript} className="max-h-[500px]" />
      </GlassCard>

      {/* Full Tool Timeline */}
      <GlassCard
        variant="default"
        header={
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-warning" />
              <span className="text-sm font-semibold text-text-primary">Tool Call Details</span>
            </div>
            <span className="text-xs text-text-muted">
              {tools.length} calls · {tools.filter((t) => t.status === 'success').length} success ·{' '}
              {tools.filter((t) => t.status === 'error').length} errors
            </span>
          </div>
        }
      >
        <ToolTimeline tools={tools} />
      </GlassCard>
    </motion.div>
  );
}


